'use client';

import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { type Employer, useUserContext } from '@instamenu/context-user';
import {
  Timespan,
  deserializeTimespan,
  serializeTimespan,
  timespans,
} from '@/components/core/forms/timespans';
import { TimeRange, allDay } from '@/components/filters/CustomTimeRange';

type LocalStorageType = {
  timespan: string;
  futureTimespan: string;
  restaurant?: string;
  restaurants?: string[];
  date: string;
  custom: boolean;
  times: TimeRange;
};

const toLocalStorage = (updates: Partial<LocalStorageType>, key = 'filters') => {
  const existing = fromLocalStorage(key);
  localStorage?.setItem(key, JSON.stringify({ ...existing, ...updates }));
};

const fromLocalStorage = (key = 'filters'): LocalStorageType | undefined => {
  if (typeof window !== 'undefined') {
    const raw = localStorage.getItem(key);
    if (!raw) return;
    try {
      return JSON.parse(raw);
    } catch {
      return;
    }
  }
};

export type RestaurantsFilterContext = {
  restaurant: Employer;
  setRestaurant: (value: Employer) => void;
  restaurants: Employer[];
  setRestaurants: (value: Employer[]) => void;
  timespan: Timespan;
  setTimespan: (value: Timespan) => void;
  futureTimespan: Timespan;
  setFutureTimespan: (value: Timespan) => void;
  times: TimeRange;
  setTimes: (value: TimeRange) => void;
  memoizedQueryVars: { restaurantIds: string[] };
  setRestaurantById: (id: string) => void;
};

export const DataFiltersProvider = ({ children }: PropsWithChildren) => {
  const searchParams = useSearchParams();
  const timespanQuery = searchParams.get('timespan');

  const router = useRouter();
  const pathname = usePathname();

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(name, value);
      return params.toString();
    },
    [searchParams],
  );

  const { restaurants: userRestaurants } = useUserContext();
  const [restaurants, setRestaurantsState] = useState<Employer[]>(() => {
    const data = fromLocalStorage();
    if (!userRestaurants) return [];
    if (!data?.restaurants) return userRestaurants;
    return data.restaurants
      .map((id: string) => userRestaurants.find((r) => id === r.id))
      .flatMap((x) => (x ? [x] : []));
  });
  const [restaurant, setRestaurantState] = useState<Employer>(() => {
    const data = fromLocalStorage();
    return (
      (data?.restaurant ? userRestaurants.find((r) => r.id === data.restaurant) : undefined) ??
      userRestaurants[0]
    );
  });

  const setRestaurant = (value: Employer) => {
    toLocalStorage({ restaurant: value.id });
    setRestaurantState(value);
  };

  const setRestaurantById = useCallback(
    (id: string) => {
      const r = userRestaurants.find((r) => r.id === id);
      if (r) setRestaurant(r);
    },
    [userRestaurants],
  );

  const setRestaurants = (value: Employer[]) => {
    toLocalStorage({ restaurants: value.map(({ id }) => id) });
    setRestaurantsState(value);
  };

  const setTimespan = (value: Timespan) => {
    router.push(pathname + '?' + createQueryString('timespan', serializeTimespan(value)));
    toLocalStorage({ timespan: serializeTimespan(value) });
    setTimespanState(value);
  };

  const [timespan, setTimespanState] = useState(() => {
    if (timespanQuery) return deserializeTimespan(timespanQuery);
    const existing = fromLocalStorage()?.timespan;
    if (!existing) return timespans[0];
    return deserializeTimespan(existing);
  });

  const [futureTimespan, setFutureTimespanState] = useState(() => {
    const existing = fromLocalStorage('futureFilters')?.futureTimespan;
    if (!existing) return timespans[0];
    return deserializeTimespan(existing);
  });

  const setFutureTimespan = (value: Timespan) => {
    toLocalStorage({ futureTimespan: serializeTimespan(value) }, 'futureFilters');
    setFutureTimespanState(value);
  };

  const [times, setTimesState] = useState(fromLocalStorage()?.times ?? allDay);
  const setTimes = (value: TimeRange) => {
    toLocalStorage({ times: value });
    setTimesState(value);
  };

  const memoizedQueryVars = useMemo(
    () => ({
      restaurantIds: restaurants.map((restaurant) => restaurant.id),
    }),
    [restaurants],
  );

  return (
    <RestaurantsFilterContext.Provider
      value={{
        memoizedQueryVars,
        restaurant,
        setRestaurant,
        restaurants,
        setRestaurants,
        timespan,
        setTimespan,
        futureTimespan,
        setFutureTimespan,
        times,
        setTimes,
        setRestaurantById,
      }}
    >
      {children}
    </RestaurantsFilterContext.Provider>
  );
};

const RestaurantsFilterContext = createContext<RestaurantsFilterContext | undefined>(undefined);

export function useDataFilters() {
  const ctx = useContext(RestaurantsFilterContext);
  if (!ctx) throw new Error('useDataFilters must be used within DataFiltersProvider');
  return ctx;
}
